import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, ArrowRight, MessageSquareQuote, Star } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../utils/supabase/client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const feedbackCategories = ['Suggestion', 'Recommendation', 'Overall experience'] as const;
const feedbackUserTypes = ['Teacher/Instructor', 'Student', 'Website Visitor'] as const;

export default function FeedbackPage() {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [category, setCategory] = useState<(typeof feedbackCategories)[number]>('Overall experience');
  const [userType, setUserType] = useState<(typeof feedbackUserTypes)[number] | ''>('');
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedMessage = message.trim();
    const trimmedName = displayName.trim();

    if (trimmedMessage.length < 5 || trimmedMessage.length > 1000) {
      toast.error('Feedback must be between 5 and 1,000 characters.');
      return;
    }

    if (trimmedName.length > 60) {
      toast.error('Your display name must be 60 characters or fewer.');
      return;
    }

    if (rating < 1 || rating > 5) {
      toast.error('Please select a star rating from 1 to 5.');
      return;
    }

    setIsSubmitting(true);
    const { error } = await supabase.from('client_feedback').insert({
      display_name: trimmedName || 'Anonymous client',
      category,
      user_type: userType,
      rating,
      message: trimmedMessage,
    });

    if (error) {
      console.error('Failed to submit client feedback:', error);
      toast.error('Your feedback could not be submitted. Please try again.');
      setIsSubmitting(false);
      return;
    }

    toast.success('Thank you! Your feedback is now visible on the landing page.');
    navigate('/');
  }

  return (
    <main className="min-h-screen bg-[#f8f9fa] px-4 py-8 sm:px-6 sm:py-12">
      <div className="max-w-2xl mx-auto">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-sm text-[#5f6368] hover:text-[#202124] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </button>

        <div className="bg-white rounded-2xl border border-[#e0e0e0] shadow-sm p-6 sm:p-10">
          <div className="w-12 h-12 rounded-xl bg-primary/8 flex items-center justify-center mb-5">
            <MessageSquareQuote className="w-6 h-6 text-primary" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#202124] tracking-tight">
            Share your feedback
          </h1>
          <p className="text-sm sm:text-base text-[#5f6368] mt-3 leading-relaxed">
            Tell us your suggestions, recommendations, or how your experience has been.
            Submitted feedback appears publicly in the Client Feedback section.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5 mt-8">
            <div>
              <label htmlFor="feedback-name" className="block text-sm font-medium text-[#202124] mb-2">
                Display name <span className="text-[#5f6368] font-normal">(optional)</span>
              </label>
              <Input
                id="feedback-name"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                maxLength={60}
                placeholder="Anonymous client"
                autoComplete="name"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label htmlFor="feedback-user-type" className="block text-sm font-medium text-[#202124] mb-2">
                I am a
              </label>
              <select
                id="feedback-user-type"
                value={userType}
                onChange={(event) => setUserType(event.target.value as (typeof feedbackUserTypes)[number] | '')}
                className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm text-[#202124] outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                required
                disabled={isSubmitting}
              >
                <option value="" disabled>Select your user type</option>
                {feedbackUserTypes.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="feedback-category" className="block text-sm font-medium text-[#202124] mb-2">
                Feedback type
              </label>
              <select
                id="feedback-category"
                value={category}
                onChange={(event) => setCategory(event.target.value as (typeof feedbackCategories)[number])}
                className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm text-[#202124] outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                disabled={isSubmitting}
              >
                {feedbackCategories.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <fieldset>
              <legend className="block text-sm font-medium text-[#202124] mb-2">
                Your rating <span className="text-[#5f6368] font-normal">(required)</span>
              </legend>
              <div className="flex items-center gap-1" role="radiogroup" aria-label="Star rating">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={rating === value}
                    aria-label={`${value} ${value === 1 ? 'star' : 'stars'}`}
                    onClick={() => setRating(value)}
                    disabled={isSubmitting}
                    className="rounded p-1 text-amber-500 transition-colors hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:cursor-not-allowed"
                  >
                    <Star className={`w-7 h-7 ${value <= rating ? 'fill-current' : ''}`} />
                  </button>
                ))}
                <span className="ml-2 text-sm text-[#5f6368]" aria-live="polite">
                  {rating > 0 ? `${rating} out of 5` : 'Select a rating'}
                </span>
              </div>
            </fieldset>

            <div>
              <label htmlFor="feedback-message" className="block text-sm font-medium text-[#202124] mb-2">
                Your feedback
              </label>
              <Textarea
                id="feedback-message"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                minLength={5}
                maxLength={1000}
                rows={6}
                placeholder="What would you like us to know?"
                required
                disabled={isSubmitting}
              />
              <p className="text-xs text-[#5f6368] mt-2 text-right">{message.length}/1,000</p>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="rounded-full h-11 px-6"
                onClick={() => navigate('/')}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" className="rounded-full h-11 px-6" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit feedback'}
                {!isSubmitting && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}
